import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, LoadingState, PageHeader, Status, cn, formatDate, formatMoney, formatRelative } from '@lawbee/ui';
import { CalendarDays, ClipboardList, Hourglass } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '../../../lib/api';
import { formatInTz, todayIn } from '../../../lib/hooks';
import { hasFeature, useMe } from '../../../lib/me';
import type { DashboardResponse, IntakeStatus, SettingsResponse } from '../../../lib/types';
import { CopyButton } from './shared';

const INTAKE_STATUS: Record<IntakeStatus, { label: string; variant: 'primary' | 'warning' | 'info' | 'success' | 'default' }> = {
  new: { label: 'New', variant: 'primary' },
  needs_info: { label: 'Needs info', variant: 'warning' },
  in_review: { label: 'In review', variant: 'info' },
  converted: { label: 'Converted', variant: 'success' },
  declined: { label: 'Declined', variant: 'default' },
};

function greeting(timezone: string): string {
  const hour = Number(formatInTz(new Date().toISOString(), timezone, { hour: 'numeric', hourCycle: 'h23' }));
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function DashboardPage() {
  const me = useMe();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<DashboardResponse>('/api/workspace/dashboard') });
  const settings = useQuery({ queryKey: ['workspace-settings'], queryFn: () => api.get<SettingsResponse>('/api/workspace/settings') });
  const firstName = me.user.name.split(' ')[0] ?? '';

  return (
    <div>
      <PageHeader
        title={`${greeting(me.workspace.timezone)}${firstName ? `, ${firstName}` : ''}`}
        description={me.workspace.name}
        actions={
          settings.data ? (
            <>
              <CopyButton value={settings.data.publicUrls.intakeBase} label="Intake link" copiedLabel="Intake link copied" />
              <CopyButton value={settings.data.publicUrls.bookingBase} label="Booking link" copiedLabel="Booking link copied" />
            </>
          ) : null
        }
      />
      {q.isLoading ? <LoadingState label="Loading your dashboard" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data ? <DashboardBody data={q.data} /> : null}
    </div>
  );
}

function DashboardBody({ data }: { data: DashboardResponse }) {
  const me = useMe();
  const tz = me.workspace.timezone;
  const today = todayIn(tz);
  const showFlags = hasFeature(me, 'deadline_checkins');
  const showPayments = hasFeature(me, 'payments');

  const tiles: { key: string; label: string; value: ReactNode; sub?: ReactNode; to: string; search?: Record<string, string>; tone?: 'warn' }[] = [
    { key: 'new', label: 'New intakes', value: data.newIntakes, to: '/intakes', search: { status: 'new' } },
    { key: 'needs', label: 'Needs info', value: data.needsInfo, to: '/intakes', search: { status: 'needs_info' }, tone: data.needsInfo > 0 ? 'warn' : undefined },
    { key: 'consults', label: 'Consultations this week', value: data.consultationsThisWeek.length, to: '/bookings' },
    { key: 'matters', label: 'Open matters', value: data.openMatters, to: '/matters' },
    ...(showPayments
      ? [
          {
            key: 'outstanding',
            label: 'Outstanding payments',
            value: formatMoney(data.outstanding.totalCents),
            sub: `${data.outstanding.count} open ${data.outstanding.count === 1 ? 'invoice' : 'invoices'}`,
            to: '/payments',
          },
        ]
      : []),
    { key: 'approvals', label: 'Pending approvals', value: data.pendingApprovals, to: '/updates', tone: data.pendingApprovals > 0 ? 'warn' : undefined },
    ...(showFlags ? [{ key: 'flags', label: 'Open check-in flags', value: data.openFlags, to: '/checkins', tone: data.openFlags > 0 ? ('warn' as const) : undefined }] : []),
  ];

  return (
    <div className="space-y-8">
      <section aria-label="Key figures" className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {tiles.map((t) => (
          <Link
            key={t.key}
            to={t.to}
            search={t.search}
            className="group flex flex-col justify-between gap-3 rounded-xl bg-card p-4 shadow-card transition-shadow hover:shadow-[0_0_0_1px_var(--color-primary)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/30"
          >
            <span className="text-[13px] font-medium leading-snug text-muted-foreground">{t.label}</span>
            <span>
              <span className={cn('figures block text-[26px] font-semibold leading-none tracking-tight', typeof t.value === 'string' && 'text-[20px]', t.tone === 'warn' && 'text-accent-foreground')}>
                {t.value}
              </span>
              {t.sub ? <span className="figures mt-1 block text-xs text-muted-foreground">{t.sub}</span> : null}
            </span>
          </Link>
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Upcoming deadlines</CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcomingDeadlines.length === 0 ? (
              <EmptyState icon={<Hourglass />} title="No open deadlines" description="Deadlines you add to matters show up here." className="py-8" />
            ) : (
              <ul className="divide-y divide-border/70">
                {data.upcomingDeadlines.map((d) => {
                  const overdue = d.dueOn < today;
                  const isToday = d.dueOn === today;
                  return (
                    <li key={d.id} className="flex items-start justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{d.title}</p>
                        <Link to="/matters/$id" params={{ id: d.matterId }} className="block truncate text-[13px] text-muted-foreground hover:text-foreground hover:underline">
                          <span className="figures">{d.matterNumber}</span> {d.matterTitle}
                        </Link>
                      </div>
                      <div className="shrink-0 text-right">
                        {overdue ? (
                          <Status tone="blocked" className="text-destructive">
                            <span className="figures">{formatDate(d.dueOn)}</span>
                          </Status>
                        ) : (
                          <span className={cn('figures text-[13px]', isToday ? 'font-semibold text-accent-foreground' : 'text-muted-foreground')}>{isToday ? 'Today' : formatDate(d.dueOn)}</span>
                        )}
                        {overdue ? <span className="block text-xs text-destructive">Overdue</span> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>This week&apos;s consultations</CardTitle>
            <Link to="/bookings" className="text-[13px] font-medium text-accent-foreground hover:underline">
              All bookings
            </Link>
          </CardHeader>
          <CardContent>
            {data.consultationsThisWeek.length === 0 ? (
              <EmptyState icon={<CalendarDays />} title="No consultations this week" description="Share your booking link so clients can book." className="py-8" />
            ) : (
              <ul className="divide-y divide-border/70">
                {data.consultationsThisWeek.map((b) => (
                  <li key={b.id} className="flex items-start gap-3 py-3">
                    <div className="w-20 shrink-0">
                      <p className="figures text-[13px] font-medium">{formatInTz(b.startsAt, tz, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                      <p className="figures text-xs text-muted-foreground">{formatInTz(b.startsAt, tz, { hour: 'numeric', minute: '2-digit' })}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{b.name}</p>
                      <p className="truncate text-[13px] text-muted-foreground">
                        {b.typeName}
                        {b.status === 'pending_payment' ? ' · awaiting payment' : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Recent intakes</CardTitle>
            <Link to="/intakes" className="text-[13px] font-medium text-accent-foreground hover:underline">
              All intakes
            </Link>
          </CardHeader>
          <CardContent>
            {data.recentIntakes.length === 0 ? (
              <EmptyState icon={<ClipboardList />} title="No intakes yet" description="New enquiries from your intake forms appear here." className="py-8" />
            ) : (
              <ul className="divide-y divide-border/70">
                {data.recentIntakes.map((i) => {
                  const name = i.name ?? [i.firstName, i.lastName].filter(Boolean).join(' ');
                  const st = INTAKE_STATUS[i.status] ?? { label: i.status, variant: 'default' as const };
                  return (
                    <li key={i.id}>
                      <Link to="/intakes/$id" params={{ id: i.id }} className="-mx-2 flex items-start justify-between gap-3 rounded-lg px-2 py-3 hover:bg-foreground/[0.03]">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{name || 'Unnamed'}</p>
                          <p className="truncate text-[13px] text-muted-foreground">
                            {i.formName ?? 'Intake'} · <span className="figures">{formatRelative(i.createdAt)}</span>
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <Badge variant={st.variant}>{st.label}</Badge>
                          {i.missingCount > 0 ? (
                            <Badge variant="warning">
                              <span className="figures">{i.missingCount}</span> missing
                            </Badge>
                          ) : null}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
